import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useI18nContext } from "@hopper-b2b/i18n";
import { isEqual } from "lodash-es";
import { HotelsAvailabilityActions } from "../../../store";
import {
  getAmenitiesFilter,
  getCancellationFilter,
  getFilterOptions,
  getHomeAmenitiesFilter,
  getHomeRoomsAndBedsFilter,
  getHotelNameFilter,
  getMealPlanFilter,
  getPayLaterFilter,
  getPriceFilter,
  getStarRatingFilter,
  getStayTypeFilter,
  getUserRatingFilter,
} from "../../../store/selectors";

export const useFilterDialog = () => {
  const priceState = useSelector(getPriceFilter);
  const ratingState = useSelector(getStarRatingFilter);
  const cancellationState = useSelector(getCancellationFilter);
  const payLaterState = useSelector(getPayLaterFilter);
  const amenitiesState = useSelector(getAmenitiesFilter);
  const hotelNameState = useSelector(getHotelNameFilter);
  const mealPlanState = useSelector(getMealPlanFilter);
  const stayTypeState = useSelector(getStayTypeFilter);
  const homeAmenitiesState = useSelector(getHomeAmenitiesFilter);
  const homeRoomsAndBedsState = useSelector(getHomeRoomsAndBedsFilter);
  const userRatingState = useSelector(getUserRatingFilter);
  const filterOptions = useSelector(getFilterOptions);

  const [priceRange, setPriceRange] = useState(priceState);
  const [amenities, setAmenities] = useState(amenitiesState);
  const [cancellation, setCancellation] = useState(cancellationState);
  const [starRating, setStarRating] = useState(ratingState);
  const [payLater, setPayLater] = useState(payLaterState);
  const [hotelName, setHotelNameFilter] = useState(hotelNameState);
  const [mealPlan, setMealPlanFilter] = useState(mealPlanState);
  const [stayType, setStayTypeFilter] = useState(stayTypeState);
  const [homeAmenities, setHomeAmenitiesFilter] = useState(homeAmenitiesState);
  const [homeRoomsAndBeds, setHomeRoomsAndBedsFilter] = useState(
    homeRoomsAndBedsState
  );
  const [userRating, setUserRating] = useState(userRatingState);
  const dispatch = useDispatch();

  const filtersChanged = useMemo(() => {
    return (
      !isEqual(priceState, priceRange) ||
      !isEqual(ratingState, starRating) ||
      payLaterState !== payLater ||
      !isEqual(amenitiesState, amenities) ||
      cancellationState !== cancellation ||
      hotelNameState !== hotelName ||
      !isEqual(mealPlanState, mealPlan) ||
      !isEqual(stayTypeState, stayType) ||
      !isEqual(homeAmenitiesState, homeAmenities) ||
      !isEqual(homeRoomsAndBedsState, homeRoomsAndBeds) ||
      userRatingState !== userRating
    );
  }, [
    amenities,
    amenitiesState,
    cancellation,
    cancellationState,
    homeAmenities,
    homeAmenitiesState,
    homeRoomsAndBeds,
    homeRoomsAndBedsState,
    hotelName,
    hotelNameState,
    mealPlan,
    mealPlanState,
    payLater,
    payLaterState,
    priceRange,
    priceState,
    ratingState,
    starRating,
    stayType,
    stayTypeState,
    userRating,
    userRatingState,
  ]);

  const updateValuesToMatchState = () => {
    setPriceRange(priceState);
    setAmenities(amenitiesState);
    setCancellation(cancellationState);
    setStarRating(ratingState);
    setPayLater(payLaterState);
    setHotelNameFilter(hotelNameState);
    setMealPlanFilter(mealPlanState);
    setStayTypeFilter(stayTypeState);
    setHomeAmenitiesFilter(homeAmenitiesState);
    setHomeRoomsAndBedsFilter(homeRoomsAndBedsState);
    setUserRating(userRatingState);
  };

  const resetAllFilters = () => {
    dispatch(HotelsAvailabilityActions.resetFilters());
  };

  const applyFilter = () => {
    dispatch(
      HotelsAvailabilityActions.setAllFilters({
        priceRange,
        starRating,
        amenities,
        freeCancellation: cancellation,
        payLater,
        hotelName,
        mealPlan,
        stayType,
        homeAmenities,
        homeRoomsAndBeds,
        userRating,
      })
    );
  };

  const onPriceChange = ({ minPrice, maxPrice }) => {
    setPriceRange({
      min: minPrice,
      max: maxPrice,
    });
  };

  const { t } = useI18nContext();

  const text = {
    price: {
      title: t("core-hotels.availabilityPage.filters.price.title"),
      subtitle: t("core-hotels.availabilityPage.filters.price.subtitle"),
      description: t("core-hotels.availabilityPage.filters.price.description"),
    },
    cancellation: {
      title: t("core-hotels.availabilityPage.filters.cancellation.title"),
      description: t(
        "core-hotels.availabilityPage.filters.cancellation.description"
      ),
    },
    payLater: {
      title: t("core-hotels.availabilityPage.filters.payLater.title"),
      description: t(
        "core-hotels.availabilityPage.filters.payLater.description"
      ),
    },
    rating: {
      title: t("core-hotels.availabilityPage.filters.starRating.title"),
    },
    amenities: {
      title: t("core-hotels.availabilityPage.filters.amenities.title"),
    },
    hotelName: {
      title: t("core-hotels.availabilityPage.filters.hotelName.title"),
      label: t("core-hotels.availabilityPage.filters.hotelName.label"),
      description: t(
        "core-hotels.availabilityPage.filters.hotelName.description"
      ),
    },
    mealPlan: {
      title: t("core-hotels.availabilityPage.filters.mealPlan.title"),
    },
    userRating: {
      title: t("core-hotels.availabilityPage.filters.userRating.title"),
    },
  };

  return {
    text,
    context: {
      priceRange,
      amenities,
      cancellation,
      starRating,
      payLater,
      filterOptions,
      hotelName,
      mealPlan,
      stayType,
      homeAmenities,
      homeRoomsAndBeds,
      filtersChanged,
      userRating,
    },
    handlers: {
      updateValuesToMatchState,
      resetAllFilters,
      applyFilter,
      onPriceChange,
      onAmenitiesChange: setAmenities,
      onCancellationChange: setCancellation,
      onRatingChange: setStarRating,
      onPayLaterChange: setPayLater,
      onHotelNameChange: setHotelNameFilter,
      onMealPlanChange: setMealPlanFilter,
      onStayTypeChange: setStayTypeFilter,
      onHomeAmenitiesChange: setHomeAmenitiesFilter,
      onHomeBedsAndRoomsChange: setHomeRoomsAndBedsFilter,
      onUserRatingChange: setUserRating,
    },
  };
};
